import React, {Fragment, useEffect} from "react";
import {DateInput} from "@blueprintjs/datetime";
import Dictionary from "../Reservation/Dictionary";
import MomentFormatter from "../../utils/MomentFormatter";
import InputErrorMessage from "../Reservation/InputErrorMessage";
import Validator from "../../utils/Validator";
import {isPolishNationality} from "../../utils/NationalityUtils";
import FormattedDateInput from "../../components/FormattedDateInput/FormattedDateInput";

function PersonFormFieldsForCredit(props) {

    const MARRIAGE = "MARRIAGE";
    const PROPERTY_SEPARATION_NOT_APPLICABLE = "NOT_APPLICABLE";
    const PROPERTY_SEPARATION_NO = "NO";

    let {formValidator} = props;

    let emptySpouse = {
        "pesel": "",
        "email": "",
        "phoneNumber": "",
        "location": "",
        "firstName": "",
        "lastName": "",
        "identityCardType": "ID_CARD",
        "identityCardNumber": "",
        "identityCardExpirationDate": null,
        "identityCardAuthority": "",
        "street": "",
        "apartmentNumber": "",
        "postalCode": "",
        "birthDate": "",
        "nationality": "Polska"
    };

    const personChangeHandler = (event) => {
        let {name, value} = event.target;
        let nameWithoutIndex = name.split("_")[0];
        props.onChange({...props.person, [nameWithoutIndex]: value}, props.index);
    };

    const personRemoveHandler = () => {
        props.onRemove(props.index);
    };

    const birthDateChangeHandler = (date) =>
        props.onChange({...props.person, birthDate: date}, props.index);

    const IDExpirationDateChangeHandler = (date) =>
        props.onChange({...props.person, identityCardExpirationDate: date}, props.index);

    const onChange = props.onChange;

    useEffect(() => {
        if (props.person.propertySeparation) {
            if (props.person.propertySeparation === PROPERTY_SEPARATION_NO) {
                onChange({
                    ...props.person,
                    spouse: {
                        ...emptySpouse,
                        location: props.person.location,
                        street: props.person.street,
                        apartmentNumber: props.person.apartmentNumber,
                        postalCode: props.person.postalCode
                    }
                }, props.index);
            } else {
                onChange({
                    ...props.person,
                    spouse: null
                }, props.index);
            }
        }
    }, [props.person.propertySeparation]);

    const maritalStatusChangeHandler = (event) => {
        let {value} = event.target;

        if (value === MARRIAGE) {
            props.onChange({
                ...props.person,
                maritalStatus: value,
                propertySeparation: PROPERTY_SEPARATION_NO
            }, props.index);
        } else {
            props.onChange({
                ...props.person,
                maritalStatus: value,
                propertySeparation: PROPERTY_SEPARATION_NOT_APPLICABLE
            }, props.index);
        }
    };

    const spouseChangeHandler = (spouse) => {
        props.onChange({
            ...props.person,
            spouse: {
                ...spouse,
            }
        }, props.index);
    };

    let maritalStatusFields = null;

    if (props.showMaritalStatus) {
        let separationTypeOptions = Dictionary.createDictionaryOptionsValueAsKey(Dictionary.SEPARATION_TYPE);

        // remove last option for MARRIAGE marital status
        if (props.person.maritalStatus === MARRIAGE) {
            separationTypeOptions.splice(separationTypeOptions.length - 1, 1);
        }

        let spouseFields = null;
        if (props.person.spouse) {
            spouseFields = (
                <Fragment>
                    <hr className="cr-field-full"/>
                    <PersonFormFieldsForCredit
                        title="Dane małżonka"
                        person={props.person.spouse}
                        showMaritalStatus={false}
                        canRemove={false}
                        index={props.index + 100}
                        onChange={spouseChangeHandler}
                        formValidator={props.formValidator}
                    />
                </Fragment>
            );
        }

        maritalStatusFields = (
            <Fragment>
                <div className="expert-help-field">
                    <label className="expert-help-label" htmlFor={"maritalStatus_" + props.index}>Stan cywilny</label>
                    <select id={"maritalStatus_" + props.index}
                            ref={formValidator.register({required: true})}
                            name={"maritalStatus_" + props.index}
                            value={props.person.maritalStatus === null ? "" : props.person.maritalStatus}
                            onChange={maritalStatusChangeHandler}>
                        <option value=""></option>
                        {Dictionary.createDictionaryOptionsValueAsKey(Dictionary.MARITAL_STATUS)}
                    </select>
                    <InputErrorMessage error={formValidator.errors["maritalStatus_" + props.index]}/>
                </div>

                <div className="expert-help-field">
                    <label className="expert-help-label" htmlFor={"propertySeparation_" + props.index}>Rozdzielność majątkowa</label>
                    <select id={"propertySeparation_" + props.index}
                            ref={formValidator.register({required: true})}
                            name={"propertySeparation_" + props.index}
                            disabled={props.person.maritalStatus !== MARRIAGE}
                            value={props.person.propertySeparation}
                            onChange={personChangeHandler}>
                        {separationTypeOptions}
                    </select>
                    <InputErrorMessage error={formValidator.errors["propertySeparation_" + props.index]}/>
                </div>

                {spouseFields}
            </Fragment>
        );
    }

    let removeButton = null;
    if (props.canRemove) {
        removeButton = (
            <button type="button" className="cr-remove-button" onClick={personRemoveHandler}>Usuń</button>
        );
    }

    let bikStatusField = null;
    if (props.showBikStatus) {
        bikStatusField = (
            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor="bikStatus">Status w bazach BIK/KRD</label>
                <select id="bikStatus"
                        ref={formValidator.register({required: true})}
                        name="bikStatus"
                        value={props.person.bikStatus === null ? "" : props.person.bikStatus}
                        onChange={personChangeHandler}>
                    <option value=""></option>
                    {Dictionary.createDictionaryOptionsValueAsKey(Dictionary.BIK_KRD_STATUS)}
                </select>
                <InputErrorMessage error={formValidator.errors.bikStatus}/>
            </div>
        );
    }

    const isPESELRequired = () => isPolishNationality(props.person.nationality);
    const isBirthDateRequired = () => !isPolishNationality(props.person.nationality);

    return (
        <Fragment>
            <div className="cr-field-full cr-representative-title">
                <h3 className="cr-section-heading">{props.title}</h3>
                {removeButton}
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"nationality_" + props.index}>Obywatelstwo</label>
                <select id={"nationality_" + props.index}
                        ref={formValidator.register({required: true})}
                        name={"nationality_" + props.index}
                        value={props.person.nationality}
                        onChange={personChangeHandler}>
                    {Dictionary.createDictionaryOptions(Dictionary.ALL_COUNTRIES)}
                </select>
                <InputErrorMessage error={formValidator.errors["nationality_" + props.index]}/>
            </div>

            {isPESELRequired() && (
                <div className="expert-help-field">
                    <label className="expert-help-label" htmlFor={"pesel_" + props.index}>PESEL</label>
                    <input type="text"
                           id={"pesel_" + props.index}
                           ref={formValidator.register({
                               required: true,
                               pattern: /[0-9]{11}/,
                               maxLength: 11,
                               validate: value => Validator.validatePesel(value)
                           })}
                           name={"pesel_" + props.index}
                           value={props.person.pesel}
                           onChange={personChangeHandler}/>
                    <InputErrorMessage error={formValidator.errors["pesel_" + props.index]}
                                       message="Podaj poprawny numer PESEL"/>
                </div>
            )}

            {isBirthDateRequired() && (
                <div className="expert-help-field">
                    <label className="expert-help-label" htmlFor={"birthDate_" + props.index}>Data urodzenia</label>
                    <FormattedDateInput
                        name={"birthDate_" + props.index}
                        showOnlyPast={true}
                        inputRef={formValidator.register({required: true})}
                        value={props.person.birthDate}
                        onChange={birthDateChangeHandler}
                    />
                    <InputErrorMessage error={formValidator.errors["birthDate_" + props.index]}
                                       message="Wprowadź poprawną datę w formacie rrrr-mm-dd"/>
                </div>
            )}

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"firstName_" + props.index}>Imię</label>
                <input type="text"
                       id={"firstName_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"firstName_" + props.index}
                       value={props.person.firstName}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["firstName_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"lastName_" + props.index}>Nazwisko</label>
                <input type="text"
                       id={"lastName_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"lastName_" + props.index}
                       value={props.person.lastName}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["lastName_" + props.index]}/>
            </div>

            {bikStatusField}

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"identityCardType_" + props.index}>Rodzaj dowodu tożsamości</label>
                <select id={"identityCardType_" + props.index}
                        ref={formValidator.register({required: true})}
                        name={"identityCardType_" + props.index}
                        value={props.person.identityCardType}
                        onChange={personChangeHandler}>
                    {Dictionary.createDictionaryOptionsValueAsKey(Dictionary.IDENTITY_CARD_TYPE)}
                </select>
                <InputErrorMessage error={formValidator.errors["identityCardType_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"identityCardNumber_" + props.index}>Nr dowodu tożsamości</label>
                <input type="text"
                       id={"identityCardNumber_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"identityCardNumber_" + props.index}
                       value={props.person.identityCardNumber}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["identityCardNumber_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"identity-card-expiration-date_" + props.index}>Data ważności dowodu tożsamości</label>
                <DateInput
                    {...MomentFormatter.datePickerFormatProps()}
                    popoverProps={{usePortal: false}}
                    inputProps={{
                        inputRef: formValidator.register({required: true}),
                        name: "identityCardExpirationDate_" + props.index
                    }}
                    minDate={new Date()}
                    maxDate={(d => new Date(d.getFullYear() + 100, d.getMonth(), d.getDate()))(new Date())}
                    onChange={IDExpirationDateChangeHandler}
                    placeholder={"Wprowadź datę w formacie rrrr-mm-dd"}
                    value={props.person.identityCardExpirationDate}
                />
                <InputErrorMessage error={formValidator.errors["identityCardExpirationDate_" + props.index]}
                                   message="Wprowadź poprawną datę w formacie rrrr-mm-dd"/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"identityCardAuthority_" + props.index}>Organ wydający dowód tożsamości</label>
                <input type="text"
                       id={"identityCardAuthority_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"identityCardAuthority_" + props.index}
                       value={props.person.identityCardAuthority}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["identityCardAuthority_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"phoneNumber_" + props.index}>Nr telefonu</label>
                <input type="text"
                       id={"phoneNumber_" + props.index}
                       ref={formValidator.register({
                           required: true,
                           pattern: /[0-9]{9}/,
                           maxLength: 9
                       })}
                       name={"phoneNumber_" + props.index}
                       value={props.person.phoneNumber}
                       onChange={personChangeHandler}
                       placeholder="Np. 753421869 (9 cyfr)"/>
                <InputErrorMessage error={formValidator.errors["phoneNumber_" + props.index]}
                                   message="Podaj poprawny numer telefonu (9 cyfr)"/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"email_" + props.index}>Adres e-mail</label>
                <input type="email"
                       id={"email_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"email_" + props.index}
                       value={props.person.email}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["email_" + props.index]}
                                   message="Podaj poprawny adres e-mail"/>
            </div>

            <h3 className="cr-section-heading cr-field-full">Adres zamieszkania</h3>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"postalCode_" + props.index}>Kod pocztowy</label>
                <input type="text"
                       id={"postalCode_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"postalCode_" + props.index}
                       value={props.person.postalCode}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["postalCode_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"location_" + props.index}>Miejscowość</label>
                <input type="text"
                       id={"location_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"location_" + props.index}
                       value={props.person.location}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["location_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"street_" + props.index}>Ulica</label>
                <input type="text"
                       id={"street_" + props.index}
                       ref={formValidator.register({required: true})}
                       name={"street_" + props.index}
                       value={props.person.street}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["street_" + props.index]}/>
            </div>

            <div className="expert-help-field">
                <label className="expert-help-label" htmlFor={"apartmentNumber_" + props.index}>Numer mieszkania</label>
                <input type="text"
                       id={"apartmentNumber_" + props.index}
                       ref={formValidator.register}
                       name={"apartmentNumber_" + props.index}
                       value={props.person.apartmentNumber}
                       onChange={personChangeHandler}/>
                <InputErrorMessage error={formValidator.errors["apartmentNumber_" + props.index]}/>
            </div>

            {maritalStatusFields}
        </Fragment>
    );
}

export default PersonFormFieldsForCredit;
